import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, ArrowDown, ArrowUp, DownloadSimple, CircleNotch } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { formatCurrency } from "../../lib/currency";

const fmt$ = (n, cur = "USD") => formatCurrency(n, cur);

const statusColor = (s) => {
  const up = String(s || "").toUpperCase();
  if (up === "CAPTURED") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (up === "INITIATED") return "bg-zinc-100 text-zinc-700 border-zinc-200";
  if (["FAILED", "DECLINED", "CANCELLED", "VOID", "ABANDONED"].includes(up)) return "bg-red-50 text-red-700 border-red-200";
  return "bg-zinc-100 text-zinc-700 border-zinc-200";
};

const humanIntent = (intent) => {
  const map = {
    plan: "Plan subscription",
    seats: "Seat top-up",
    renewal: "Auto-renewal",
    card_update: "Card verification",
  };
  return map[intent] || intent || "Charge";
};

export default function BillingTimeline() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    api
      .get("/tap/history?months=12")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load billing history"))
      .finally(() => setLoading(false));
  }, []);

  const downloadInvoice = async (chargeId) => {
    setDownloadingId(chargeId);
    try {
      const resp = await api.get(`/tap/invoices/${chargeId}.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `smarterp-invoice-${chargeId.slice(-8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invoice not available");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className="border border-zinc-950 mt-8" data-testid="billing-timeline-section">
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
        <span>billing.timeline · last 12 months</span>
        {data && (
          <span className="tnum text-[10px] text-zinc-300" data-testid="billing-timeline-totals">
            paid {formatCurrency(data.totals.paid)} · refunded {formatCurrency(data.totals.refunded)} · net {formatCurrency(data.totals.net)}
          </span>
        )}
      </div>

      {loading && (
        <div className="p-6 text-sm text-zinc-500 flex items-center gap-2">
          <CircleNotch size={14} className="animate-spin" /> Loading history…
        </div>
      )}

      {!loading && data?.items?.length === 0 && (
        <div className="p-8 text-center" data-testid="billing-timeline-empty">
          <Receipt size={28} weight="thin" className="text-zinc-300 mx-auto mb-2" />
          <div className="text-sm text-zinc-600">No billing activity in the last 12 months yet.</div>
          <div className="text-[11px] text-zinc-400 mt-1">Once you upgrade a plan or top-up seats, it will show up here.</div>
        </div>
      )}

      {!loading && data?.items?.length > 0 && (
        <div className="divide-y divide-zinc-100" data-testid="billing-timeline-list">
          {data.items.map((it, idx) => {
            const isRefund = it.type === "refund";
            const Icon = isRefund ? ArrowUp : ArrowDown;
            const dateStr = it.created_at ? new Date(it.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
            const canDownload = !isRefund && String(it.status).toUpperCase() === "CAPTURED" && it.id;
            return (
              <div
                key={`${it.type}-${it.id || idx}`}
                data-testid={`billing-timeline-item-${idx}`}
                className="px-6 py-3 flex items-center gap-4 hover:bg-zinc-50"
              >
                <div className={`w-8 h-8 flex items-center justify-center border ${isRefund ? "border-red-200 bg-red-50" : "border-zinc-200 bg-white"}`}>
                  <Icon size={13} weight="bold" className={isRefund ? "text-red-700" : "text-zinc-950"} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {isRefund ? `Refund · ${it.reason || "requested"}` : humanIntent(it.intent)}
                    {it.plan && !isRefund && <span className="text-zinc-500 font-normal ml-1">· {it.plan}</span>}
                    {it.seats && !isRefund && <span className="text-zinc-500 font-normal ml-1">· {it.seats} seats</span>}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 tnum mt-0.5 truncate">
                    {dateStr}
                    {it.id && <span className="ml-2">· {it.id.slice(-12)}</span>}
                  </div>
                </div>
                {!isRefund && (
                  <span
                    className={`text-[10px] font-mono tracking-[0.12em] uppercase border px-2 py-0.5 ${statusColor(it.status)}`}
                    data-testid={`billing-timeline-status-${idx}`}
                  >
                    {it.status || "—"}
                  </span>
                )}
                <div className="font-display font-black text-base tnum text-right shrink-0 min-w-24">
                  {isRefund ? "−" : ""}{fmt$(it.amount, it.currency)}
                </div>
                {canDownload ? (
                  <button
                    onClick={() => downloadInvoice(it.id)}
                    disabled={downloadingId === it.id}
                    data-testid={`billing-timeline-invoice-${idx}`}
                    className="text-xs font-mono border border-zinc-200 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white px-2 py-1 inline-flex items-center gap-1 disabled:opacity-50 transition-colors"
                    title="Download PDF invoice"
                  >
                    {downloadingId === it.id ? <CircleNotch size={11} className="animate-spin" /> : <DownloadSimple size={11} />}
                    PDF
                  </button>
                ) : (
                  <span className="w-[54px]" aria-hidden />
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
